import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Grid, Paper, styled, Typography } from '@mui/material';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { LineChart } from '@mui/x-charts';
import MessagePreview from './MessagePreview.tsx';
import { useRecoilState } from 'recoil';
import { WhatsAppTempFormAtom } from '../../../../Services/_states/index';
import { TemplateActions } from '../../../../Services/_actions/index';
import { useParams } from 'react-router-dom';
import { dateFormate } from '../../../../utils/helper';
import { calculatePercentageOfTotal } from '../../Template/template.constant.js';
import CommonDatePicker from '../../../FlowBuilder/Common/Datepicker/index.tsx';
import CommonTable from '../../../Shared/Tables/TableV1.tsx';
import DateRangeFilter from '../../../Shared/DateRangeFilter/DateRangeFilter.tsx';

// Styled Component for Paper
const StyledPaper = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    color: theme.palette.text.primary,
}));

// Type Definitions
type MessageCounts = {
    sent_count?: number;
    delivered_count?: number;
    read_count?: number;
    sent_date?: string;
    failed_count?: number;
    button_count_1?: number;
    button_count_2?: number;
    button_count_3?: number;
};

type Button = {
    name?: string;
};

interface SummaryData {
    message_counts: MessageCounts;
}

type ResponseData = {
    button_list?: Button[];
    message_counts?: MessageCounts;
};

type GraphDataInput = {
    data?: MessageCounts[];
};

type SeriesItem = {
    data: number[];
    label: string;
};

type XAxisItem = {
    scaleType: string;
    data: string[];
};

type GraphDataObject = {
    series: SeriesItem[];
    xAxis: XAxisItem[];
};

type ButtonEngagementData = {
    label: string;
    type: string;
    totalClicks: number;
    clickRate: string;
};

// Main Component
const InsightsWithMessagePreview: React.FC = () => {
    const [buttonEngagementData, setButtonEngagementData] = useState<ButtonEngagementData[]>([]);
    const { templateName, botNumber } = useParams<{ templateName: string; botNumber: string }>();
    const actions = TemplateActions();
    const [isLoading, setIsLoading] = useState(false);
    const [summaryData, setSummaryData] = useState<SummaryData>({ message_counts: {} });
    const [filtersObj, setFiltersObj] = useState({
        template_id: '',
        search: '',
        page: 0,
        limit: 20,
        start_date: null as string | null,
        end_date: null as string | null,
        sort_by: 'sent_date',
        order: 'desc',
    });

    const [whatsappTempFormData, setWhatsappTempFormData] = useRecoilState(WhatsAppTempFormAtom);
    const [graphData, setGraphData] = useState<GraphDataObject>();
    // Define Table Columns
    const columns = useMemo<MRT_ColumnDef<ButtonEngagementData>[]>(() => [
        { accessorKey: 'label', header: 'Label', enableSorting: false, Cell: ({ cell }) => <div style={{ textAlign: 'left' }}>{cell.getValue<string>()}</div> },
        { accessorKey: 'type', header: 'Type', enableSorting: false, Cell: ({ cell }) => <div style={{ textAlign: 'left' }}>{cell.getValue<string>()}</div> },
        { accessorKey: 'totalClicks', header: 'Total Clicks', enableSorting: false, Cell: ({ cell }) => <div style={{ textAlign: 'left' }}>{cell.getValue<string>()}</div> },
        { accessorKey: 'clickRate', header: 'Click Rate', enableSorting: false, Cell: ({ cell }) => <div style={{ textAlign: 'left' }}>{cell.getValue<string>()}</div> },
    ], []);

    // Create Graph Data Object
    const createGraphDataObj = (data: GraphDataInput | undefined): void => {
        if (!data?.data?.length) {
            setGraphData(undefined);
            return;
        }

        const graphData: GraphDataObject = {
            series: [
                { data: data.data.map(item => item.sent_count || 0), label: 'Sent' },
                { data: data.data.map(item => item.delivered_count || 0), label: 'Delivered' },
                { data: data.data.map(item => item.read_count || 0), label: 'Read' },
            ],
            xAxis: [{ scaleType: 'point', data: data.data.map(item => item.sent_date || 'N/A') }],
        };

        setGraphData(graphData);
    };

    // Fetch Template Data
    const getTemplateData = async () => {
        try {
            const res = await actions.getTemplateByNameAndBotNumber(templateName, botNumber);
            if (res?.status === 200) {
                setWhatsappTempFormData(res.data);
                setFiltersObj(prev => ({ ...prev, template_id: res.data.md_id }));
                await getTemplateDateWiseSummary({ ...filtersObj, template_id: res.data.md_id });
            }
        } catch (error) {
            console.error('Error fetching template data:', error);
        }
    };


    // Fetch Template Date-Wise Summary
    const getTemplateDateWiseSummary = async (filters: typeof filtersObj) => {
        setIsLoading(true);
        try {
            const updatedFilters = {
                ...filters,
                start_date: filters.start_date ? dateFormate(filters.start_date, 'DD/MM/YYYY') : '',
                end_date: filters.end_date ? dateFormate(filters.end_date, 'DD/MM/YYYY') : '',
            };

            const res = await actions.getTemplateDateWiseSummary(updatedFilters);
            if (res) {
                setSummaryData(res);
                createGraphDataObj(res);
                const totalButtonCount = res?.message_counts?.button_count_1 + res?.message_counts?.button_count_2 + res?.message_counts?.button_count_3;
                if (res?.button_list?.length) {
                    const buttonEngagementData: ButtonEngagementData[] = res?.button_list?.slice(0, 3)?.map((button: { name?: string }, index) => ({
                        label: button?.name || '',
                        type: 'Quick reply',
                        totalClicks: res.message_counts?.[`button_count_${index + 1}`] || 0,
                        clickRate: `${((res.message_counts?.[`button_count_${index + 1}`] / totalButtonCount) * 100).toFixed(2)}%`,
                    }));
                    setButtonEngagementData(buttonEngagementData);
                }
            }
        } catch (error) {
            console.error('Error fetching summary data:', error);
        } finally {
            setIsLoading(false);
        }
    };
    // console.log(graphData?.xAxis); // Inspect the structure of the xAxis
    console.log('X Axis Data:', graphData?.xAxis);
    console.log('Series Data:', graphData?.series);

    // Component Mount Effect
    useEffect(() => {
        getTemplateData();
    }, []);

    // JSX
    return (
        <Box sx={{ flexGrow: 1, pt: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={8} md={8} lg={8}>
                    <StyledPaper>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 , lineHeight:{xs:'1.2', sm:'auto'}}}>
                                Performance Overview
                            </Typography>
                            <Box>
                                {/* <CommonDatePicker isRangePicker={true} onChange={(e:any) => { 
                                    getTemplateDateWiseSummary({ ...filtersObj, start_date: e?.startDate?.$d ||'', end_date: e?.endDate?.$d || '' });
                                    setFiltersObj({ ...filtersObj, start_date: e?.startDate?.$d ||'', end_date: e?.endDate?.$d })}} /> */}
                                <DateRangeFilter 
                                    onApply={(e: any) => {
                                        const formattedStart =
                                            e?.startDate instanceof Date && !isNaN(e.startDate.getTime())
                                                ? dateFormate(e.startDate, 'DD/MM/YYYY')
                                                : '';

                                        const formattedEnd =
                                            e?.endDate instanceof Date && !isNaN(e.endDate.getTime())
                                                ? dateFormate(e.endDate, 'DD/MM/YYYY')
                                                : '';

                                        getTemplateDateWiseSummary({
                                            ...filtersObj,
                                            start_date: formattedStart,
                                            end_date: formattedEnd,
                                        });

                                        setFiltersObj({
                                            ...filtersObj,
                                            start_date: formattedStart,
                                            end_date: formattedEnd,
                                        });
                                    }}
                                />

                            </Box>
                        </Box>
                        <Grid container spacing={2}>
                            {['Messages Sent', 'Messages Delivered', 'Messages Read', 'Messages Failed'].map((label, index) => (
                                <Grid item xs={12} sm={3} md={3} lg={3} key={label}>
                                    <Card sx={{ boxShadow: 'none', backgroundColor: '#f5f5f5',p:{xs:'0px', sm:'0px',md:'8px',lg:'0px',} , textAlign:{xs:"center" , sm:"auto"} }}>
                                        <CardContent>
                                            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                                                {label}
                                            </Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                                {[
                                                    summaryData?.message_counts?.sent_count,
                                                    summaryData?.message_counts?.delivered_count,
                                                    summaryData?.message_counts?.read_count,
                                                    summaryData?.message_counts?.failed_count,
                                                ][index] || 0}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        {graphData?.series?.some(series => series.data.some(value => value > 0)) && (
                        <Box sx={{ mt: 2 }} className="chart-container-style">
                            <LineChart
                                width={600}
                                height={300}
                                series={graphData?.series || []}
                                xAxis={graphData?.xAxis || []}
                            />
                        </Box>
                        )}
                    </StyledPaper>
                </Grid>
                <Grid item xs={12} sm={4} md={4} lg={4}>
                    <StyledPaper>
                        <MessagePreview />
                    </StyledPaper>
                </Grid>
                {buttonEngagementData?.length > 0 &&
                    <Grid item xs={12}>
                        {/* <StyledPaper> */}
                        <CommonTable
                            columns={columns}
                            data={buttonEngagementData || []}
                            enableDensityToggle={false}
                            enableFullScreenToggle={false}
                            enableColumnActions={false}
                            enableColumnFilters={false}
                            enableHiding={false}
                            enableTableFooter={false}
                            enableBottomToolbar={false}
                            enablePagination={false}
                            initialState={{
                                density: 'compact',
                            }}
                            muiTableBodyCellProps={{
                                sx: (theme) => ({
                                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
                                    textAlign: 'center',
                                }),
                            }}
                            muiTableHeadCellProps={{
                                sx: {
                                    backgroundImage: 'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
                                    color: 'white',
                                },
                            }}
                            renderTopToolbar={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                                        Button Engagement
                                    </Typography>
                                </Box>
                            }

                        />
                        {/* </StyledPaper> */}
                    </Grid>
                }
            </Grid>
        </Box>
    );
};

export default InsightsWithMessagePreview;
